import {useRef, useState} from 'react';
import dateFormat from 'dateformat';
import getDaysListForMonth from '@stencil/date-utils/src/getDaysListForMonth';
import modifyDateForDate from '@stencil/date-utils/src/modifyDateForDate';
import getMonthDifferenceBetweenTwoDates from '@stencil/date-utils/src/getMonthDifferenceBetweenTwoDates';

import ArrowLeftIcon from './ArrowLeftIcon';
import ArrowRightIcon from './ArrowRightIcon';
import * as S from './dcalendar-new.styles';
import {defaultTheme} from '@stencil/build-theme';

function getMonthName(monthIndex: string): string {
  const month = Number.parseInt(monthIndex) + 1;
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  if (month >= 1 && month <= 12) {
    return monthNames[month - 1];
  }
  return '';
}

interface IMonthsViewProps {
  renderDateItem: Function;
  dateConfigToBeDisplayed: Date;
}

export const MonthView = (props: IMonthsViewProps) => {
  const {renderDateItem, dateConfigToBeDisplayed} = props;

  const dateConfigForMonth = new Date(dateConfigToBeDisplayed);
  dateConfigForMonth.setDate(1);

  const daysList = getDaysListForMonth(dateConfigForMonth);

  return (
    <>
      {daysList.map(item =>
        renderDateItem({
          value: item,
          dateObj: (item && modifyDateForDate(dateConfigForMonth, item)) || null,
        }),
      )}
    </>
  );
};

interface ICalendarViewProps {
  renderDateItem: Function;
  dateConfigForMonthToBeDisplayed: Date;
  minDateAllowed: Date;
  maxDateAllowed: Date;
  customStyles?: object;
}

export const CalendarViewNew = (props: ICalendarViewProps) => {
  const {
    renderDateItem,
    dateConfigForMonthToBeDisplayed: dateConfigForMonthToBeDisplayedFromProps,
    minDateAllowed,
    maxDateAllowed,
    customStyles = {},
  } = props;
  const ref = useRef();

  const tempDateObjToBeUsedForMonthToBeDisplayedArg = new Date(
    dateConfigForMonthToBeDisplayedFromProps,
  );
  tempDateObjToBeUsedForMonthToBeDisplayedArg.setDate(1);

  const [
    hideLeftArrow,
    hideRightArrow,
    leftArrowClickHandler,
    rightArrowClickHandler,
    dateConfigForMonthToBeDisplayed,
    dateConfigForNextMonthToBeDisplayed,
  ] = useCalendarView({
    dateConfigForMonthToBeDisplayedFromProps: tempDateObjToBeUsedForMonthToBeDisplayedArg,
    minDateAllowed,
    maxDateAllowed,
  });

  const currentMonthData = getMonthData(dateConfigForMonthToBeDisplayed);
  const nextMonthData = getMonthData(dateConfigForNextMonthToBeDisplayed);
  const days = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

  return (
    <>
      <S.CalenderOuterWrapper style={{...customStyles?.outerWrapper}}>
        <S.MonthOuterWrapper>
          <S.CalenderMonthContainer>
            <S.LeftMonthHeaderWrapper>
              <S.CircularWrapperLeft
                onClick={() => {
                  leftArrowClickHandler();
                }}
                data-testid="calendarLeftArrowBtn"
                disabled={hideLeftArrow}
              >
                <ArrowLeftIcon
                  width="1.3rem"
                  height="1.3rem"
                  fill={defaultTheme.colors.goBrandBlue.dark}
                />
              </S.CircularWrapperLeft>
              <S.MonthName data-testid="currentCalendarMonthName">
                {currentMonthData.name}
              </S.MonthName>
            </S.LeftMonthHeaderWrapper>

            <S.CalendarMainWrapperDiv>
              <S.DayAndDateLeftWrapper>
                <S.DayWrapDiv>
                  {days.map(day => (
                    <li>{day}</li>
                  ))}
                </S.DayWrapDiv>

                <S.DateWrapDiv>
                  <MonthView
                    renderDateItem={renderDateItem}
                    dateConfigToBeDisplayed={currentMonthData.beginningOfMonth}
                  />
                </S.DateWrapDiv>
              </S.DayAndDateLeftWrapper>
            </S.CalendarMainWrapperDiv>
          </S.CalenderMonthContainer>

          <S.CalenderMonthContainer>
            <S.LeftMonthHeaderWrapper>
              <S.MonthName data-testid="nextCalendarMonthName">{nextMonthData.name}</S.MonthName>
              <S.CircularWrapperRight
                onClick={() => {
                  rightArrowClickHandler();
                }}
                data-testid="calendarRightArrowBtn"
                disabled={hideRightArrow}
              >
                <ArrowRightIcon
                  width="1.3rem"
                  height="1.3rem"
                  fill={defaultTheme.colors.goBrandBlue.dark}
                />
              </S.CircularWrapperRight>
            </S.LeftMonthHeaderWrapper>

            <S.CalendarMainWrapperDiv>
              <S.DayAndDateRightWrapper>
                <S.DayWrapDiv>
                  {days.map(day => (
                    <li>{day}</li>
                  ))}
                </S.DayWrapDiv>

                <S.DateWrapDiv>
                  <MonthView
                    renderDateItem={renderDateItem}
                    dateConfigToBeDisplayed={nextMonthData.beginningOfMonth}
                  />
                </S.DateWrapDiv>
              </S.DayAndDateRightWrapper>
            </S.CalendarMainWrapperDiv>
          </S.CalenderMonthContainer>
        </S.MonthOuterWrapper>
      </S.CalenderOuterWrapper>
    </>
  );
};

const getMonthData = (date: Date) => {
  const name = dateFormat(date, 'mmmm yyyy');
  const beginningOfMonth = new Date(date);
  beginningOfMonth.setDate(1);
  return {
    name,
    beginningOfMonth,
  };
};

function useCalendarView({
  dateConfigForMonthToBeDisplayedFromProps,
  minDateAllowed,
  maxDateAllowed,
}) {
  const [dateConfigForMonthToBeDisplayed, setDateConfigForMonthToBeDisplayed] = useState(
    dateConfigForMonthToBeDisplayedFromProps,
  );

  const hideLeftArrow =
    getMonthDifferenceBetweenTwoDates(minDateAllowed, dateConfigForMonthToBeDisplayed) === 0;

  const hideRightArrow =
    getMonthDifferenceBetweenTwoDates(dateConfigForMonthToBeDisplayed, maxDateAllowed) === 0;

  const leftArrowClickHandler = () => {
    if (hideLeftArrow) return;

    dateConfigForMonthToBeDisplayed.setMonth(dateConfigForMonthToBeDisplayed.getMonth() - 1);
    setDateConfigForMonthToBeDisplayed(new Date(dateConfigForMonthToBeDisplayed));
  };

  const rightArrowClickHandler = () => {
    if (hideRightArrow) return;

    dateConfigForMonthToBeDisplayed.setMonth(dateConfigForMonthToBeDisplayed.getMonth() + 1);
    setDateConfigForMonthToBeDisplayed(new Date(dateConfigForMonthToBeDisplayed));
  };

  return [
    hideLeftArrow,
    hideRightArrow,
    leftArrowClickHandler,
    rightArrowClickHandler,
    dateConfigForMonthToBeDisplayed,
    new Date(
      dateConfigForMonthToBeDisplayed.getFullYear(),
      dateConfigForMonthToBeDisplayed.getMonth() + 1,
      dateConfigForMonthToBeDisplayed.getDate(),
    ),
  ];
}
